import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios from 'axios'
import { getTariffApi, getTariffsApi } from '../../apis/apis'
import type {
    DetailedTariffItem,
    GetTariffsArgs,
    GetTariffsListData,
    TariffItem,
    TariffStatusCounts,
} from '../../apis/types'
import type { AppDispatch, RootState } from '../../shared/redux/store'
import type { Paginated } from '../../apis/types'

const SLICE_NAME = 'TARIFFS_SLICE'

type TariffThunkReject = { err: boolean; status?: number }

function toTariffRejectValue(err: unknown): TariffThunkReject {
    if (axios.isAxiosError(err) && err.response?.status != null) {
        return { err: true, status: err.response.status }
    }
    return { err: err !== undefined }
}

export const getTariffs = createAsyncThunk<
    GetTariffsListData,
    GetTariffsArgs,
    { state: RootState; dispatch: AppDispatch; rejectValue: TariffThunkReject }
>(`${SLICE_NAME}/getTariffs`, async (query, { rejectWithValue }) => {
    try {
        return await getTariffsApi(query)
    } catch (err) {
        return rejectWithValue(toTariffRejectValue(err))
    }
})

export type GetTariffArg = string | { tariffId: string; historyCuid?: string }

function resolveGetTariffArg(arg: GetTariffArg): { tariffId: string; historyCuid?: string } {
    if (typeof arg === 'string') {
        return { tariffId: arg }
    }
    return arg
}

export const getTariff = createAsyncThunk<
    DetailedTariffItem,
    GetTariffArg,
    { state: RootState; dispatch: AppDispatch; rejectValue: TariffThunkReject }
>(`${SLICE_NAME}/getTariff`, async (arg, { rejectWithValue }) => {
    const { tariffId, historyCuid } = resolveGetTariffArg(arg)
    try {
        return await getTariffApi(tariffId, historyCuid != null ? { historyCuid } : undefined)
    } catch (err) {
        return rejectWithValue(toTariffRejectValue(err))
    }
})

interface State {
    listLoading: boolean
    detailLoading: boolean
    listError: boolean
    detailError: boolean
    listRequestId: string | null
    detailRequestId: string | null
    tariffs: Paginated<TariffItem> | undefined
    statusCounts: TariffStatusCounts | undefined
    pendingPastEffectiveCount: number
    tariff: DetailedTariffItem | undefined
}

const initialState: State = {
    listLoading: false,
    detailLoading: false,
    listError: false,
    detailError: false,
    listRequestId: null,
    detailRequestId: null,
    tariffs: undefined,
    statusCounts: undefined,
    pendingPastEffectiveCount: 0,
    tariff: undefined,
}

const slice = createSlice({
    name: SLICE_NAME,
    initialState,
    reducers: {
        clearTariffDetail: (state) => {
            state.tariff = undefined
            state.detailError = false
            state.detailLoading = false
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getTariffs.pending, (state, action) => {
            state.listLoading = true
            state.listError = false
            state.listRequestId = action.meta.requestId
        })
        builder.addCase(getTariffs.fulfilled, (state, action) => {
            if (state.listRequestId !== action.meta.requestId) {
                return
            }
            state.listLoading = false
            state.listError = false
            const { statusCounts, pendingPastEffectiveCount, ...page } = action.payload
            state.tariffs = page
            state.statusCounts = statusCounts
            state.pendingPastEffectiveCount = pendingPastEffectiveCount ?? 0
        })
        builder.addCase(getTariffs.rejected, (state, action) => {
            if (state.listRequestId !== action.meta.requestId) {
                return
            }
            if (action.meta.aborted) {
                return
            }
            const { payload, error } = action
            state.listLoading = false
            state.listError = payload != null ? payload.err : error !== undefined
        })
        builder.addCase(getTariff.pending, (state, action) => {
            state.detailLoading = true
            state.detailError = false
            state.detailRequestId = action.meta.requestId
            const { tariffId } = resolveGetTariffArg(action.meta.arg)
            if (state.tariff != null && state.tariff.cuid !== tariffId) {
                state.tariff = undefined
            }
        })
        builder.addCase(getTariff.fulfilled, (state, action) => {
            if (state.detailRequestId != null && state.detailRequestId !== action.meta.requestId) {
                return
            }
            const { tariffId } = resolveGetTariffArg(action.meta.arg)
            if (action.payload.cuid !== tariffId) {
                return
            }
            state.detailLoading = false
            state.detailError = false
            state.tariff = action.payload
        })
        builder.addCase(getTariff.rejected, (state, action) => {
            if (state.detailRequestId != null && state.detailRequestId !== action.meta.requestId) {
                return
            }
            if (action.meta.aborted) {
                return
            }
            const { tariffId } = resolveGetTariffArg(action.meta.arg)
            if (state.tariff != null && state.tariff.cuid !== tariffId) {
                return
            }
            const { payload, error } = action
            state.detailLoading = false
            state.detailError = payload != null ? payload.err : error !== undefined
        })
    },
})

export const { clearTariffDetail } = slice.actions
export const tariffsReducer = slice.reducer
